import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Shield, Lock } from 'lucide-react';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';
import type { UpgradeModalVariant } from '../../contexts/UpgradeModalContext';
import { useCheckout } from '../../hooks/useCheckout';
import type { CheckoutPlanId } from '../../data/pricingPlans';
import { PricingPlans } from '../Pricing/PricingPlans';
import { CheckoutAuthPrompt } from '../Pricing/CheckoutAuthPrompt';
interface PromptCopy {
  eyebrow: string;
  title: string;
  body: string;
  primaryLabel: string;
  primaryAction: 'free_scan' | 'single_scan' | 'invite';
}
const PROMPTS: Record<Exclude<UpgradeModalVariant, 'plans'>, PromptCopy> = {
  welcome: {
    eyebrow: 'Access granted',
    title: 'Welcome to ToxicDetector AI 🛡️',
    body: 'Start with your free forensic scan or unlock unlimited analysis.',
    primaryLabel: 'Start free scan',
    primaryAction: 'free_scan'
  },
  free_scan_complete: {
    eyebrow: 'Scan complete',
    title: 'Want deeper analysis?',
    body: 'Your free scan is complete. Unlock the full forensic report for $2.99 or get unlimited access with a subscription.',
    primaryLabel: 'Get full report — $2.99',
    primaryAction: 'single_scan'
  },
  no_credits: {
    eyebrow: 'No credits left',
    title: "You're out of free scans",
    body: 'Share ToxicDetector with a friend and earn 5 free credits, or unlock unlimited analysis.',
    primaryLabel: 'Invite a friend',
    primaryAction: 'invite'
  }
};
export function UpgradeModal() {
  const {
    variant,
    isOpen,
    closeUpgradeModal
  } = useUpgradeModal();
  const navigate = useNavigate();
  const {
    onCheckout,
    pendingPlan,
    authPromptPlan,
    dismissAuthPrompt
  } = useCheckout('upgrade_modal');
  const [showPlans, setShowPlans] = useState(false);
  useEffect(() => {
    setShowPlans(variant === 'plans');
  }, [variant]);
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeUpgradeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, closeUpgradeModal]);
  const prompt = variant && variant !== 'plans' ? PROMPTS[variant] : PROMPTS.welcome;
  const goTo = (path: string) => {
    closeUpgradeModal();
    navigate(path);
  };
  const handlePrimary = () => {
    if (prompt.primaryAction === 'free_scan') return goTo('/upload');
    if (prompt.primaryAction === 'invite') return goTo('/dashboard/referrals');
    void onCheckout('single_scan');
  };
  const handleCheckout = (plan: CheckoutPlanId) => {
    void onCheckout(plan);
  };
  return <AnimatePresence data-id="element-2928" data-mp="rnaeia25ju7l">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-2929" data-mp="rolfy384kpvj">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={closeUpgradeModal} className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-2930" data-mp="mhexbboa0oq4" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="upgrade-modal-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className={`relative z-10 max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-gray-800 bg-[#050505] p-6 sm:rounded-2xl sm:p-8 ${showPlans ? 'max-w-5xl' : 'max-w-lg'}`} data-id="element-2931" data-mp="3ryu2gl7ii60">
            <button type="button" onClick={closeUpgradeModal} aria-label="Close" className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 transition-colors duration-150 ease-out hover:bg-white/5 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" data-id="element-2932" data-mp="1roy7xhoqv758">
              <X size={18} data-id="element-2933" data-mp="2fcxxwfe8etir" />
            </button>

            {showPlans ? <div data-id="element-2934" data-mp="2aewetugt2u7a">
                <header className="mb-7 max-w-xl pr-10" data-id="element-2935" data-mp="2afpgwi4tkvj3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF0000]" data-id="element-2936" data-mp="kzq14i6p47uh">
                    Clearance levels
                  </p>
                  <h2 id="upgrade-modal-title" className="mt-2 text-2xl font-black uppercase tracking-tight text-white" data-id="element-2937" data-mp="g2zpc71xbyev">
                    Choose your access
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-2938" data-mp="2hz8i84hxlbgh">
                    Every plan runs the same forensic engine — pick how much
                    clarity you need.
                  </p>
                </header>
                <PricingPlans onCheckout={handleCheckout} pendingPlan={pendingPlan} freeHref="/upload" data-id="element-2939" data-mp="28wfozvp6upo0" />
              </div> : <div data-id="element-2940" data-mp="1z1ns6u3fk6ua">
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[#FF0000]/30 bg-[#FF0000]/10" data-id="element-2941" data-mp="1z27c2gcn55ay">
                  <Shield className="h-5 w-5 text-[#FF0000]" data-id="element-2942" data-mp="3tex56vv6qaha" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500" data-id="element-2943" data-mp="282ppszqh3go">
                  {prompt.eyebrow}
                </p>
                <h2 id="upgrade-modal-title" className="mt-2 text-2xl font-black uppercase tracking-tight text-white" data-id="element-2944" data-mp="roz94v5s4es">
                  {prompt.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-gray-400" data-id="element-2945" data-mp="3iw0bvnmtqyt5">
                  {prompt.body}
                </p>

                <div className="mt-7 flex flex-col gap-3 sm:flex-row" data-id="element-2946" data-mp="jh4zdr2xluuo">
                  <button type="button" autoFocus onClick={handlePrimary} disabled={pendingPlan !== null} className="flex-1 rounded-xl bg-[#FF0000] px-5 py-4 text-sm font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-2947" data-mp="ehsdb8jvwrt5">
                    {pendingPlan ? 'Processing…' : prompt.primaryLabel}
                  </button>
                  <button type="button" onClick={() => setShowPlans(true)} className="flex-1 rounded-xl border border-gray-700 bg-transparent px-5 py-4 text-sm font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-2948" data-mp="2l3itc1yukvhu">
                    View plans
                  </button>
                </div>

                <p className="mt-5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-gray-600" data-id="element-2949" data-mp="38sljjy7jrwny">
                  <Lock size={10} aria-hidden="true" data-id="element-2950" data-mp="3cvhwb8aeoyjz" /> Secure checkout · Cancel
                  anytime
                </p>
              </div>}
          </motion.div>

          <CheckoutAuthPrompt plan={authPromptPlan} onClose={dismissAuthPrompt} data-id="element-2951" data-mp="3ms4gduuwrlky" />
        </div>}
    </AnimatePresence>;
}